import React, { useState, useEffect, useRef } from "react";
import { IonPage, IonContent, IonButton, IonItem, IonLabel, IonModal } from "@ionic/react";
import { Helmet } from "react-helmet";

const getRandomTime = () => {
    const hours = Math.floor(Math.random() * 12) + 1; // 1 to 12
    const minutes = Math.floor(Math.random() * 12) * 5; // 0, 5, 10, ..., 55
    return { hours, minutes };
};

// Format time as "hh:mm"
const formatTime = ({ hours, minutes }: { hours: number; minutes: number }) => {
    return `${hours}:${minutes.toString().padStart(2, "0")}`;
};

// Generate 3 incorrect answers that are different from the correct one
const generateOptions = (correctTime: string) => {
    const options = new Set<string>();
    options.add(correctTime);

    while (options.size < 3) {
        const randomTime = formatTime(getRandomTime());
        if (!options.has(randomTime)) {
            options.add(randomTime);
        }
    }
    return Array.from(options).sort(() => Math.random() - 0.5); // Shuffle
};

const StaticClock: React.FC = () => {
    const [time, setTime] = useState(getRandomTime());
    const [options, setOptions] = useState<string[]>([]);
    const [selected, setSelected] = useState<string | null>(null);
    const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);
    document.title = "Choose the Correct Time – Clock Reading Practice for UKG | Mama’s Guidance";

    useEffect(() => {
        const correctTime = formatTime(time);
        setOptions(generateOptions(correctTime));
    }, [time]);

    const handleOptionClick = (option: string) => {
        setSelected(option);
        if (option === formatTime(time)) {
            setShowImage(true);
            setTimeout(() => {
                setTime(getRandomTime());
                setShowImage(false);
                setFeedback("")
            }, 5000)
        } else {
            setShowImage(false);
            setFeedback("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Help UKG children learn to read clocks and choose the correct time. Fun time-telling worksheet activities using analog clocks for early learners." />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/time/choose-correct-time" />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord pickColor matchImage" style={{ textAlign: "center", padding: "20px" }}>
                            {/* <h2>What time is shown on the clock?</h2> */}
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Choose the Correct Time</h1>

                                    <p>
                                        Understanding time is a valuable life skill. This <strong>“Choose the Correct Time”</strong> activity helps UKG students identify and match time on analog clocks. With easy-to-read visuals and guided practice, your child will learn how to tell time in a fun and interactive way!
                                    </p>

                                    <h2>🕒 What Will Your Child Learn?</h2>
                                    <ul>
                                        <li>Reading time from analog clocks (o&apos;clock times like 1:00, 2:00, 3:00, etc.)</li>
                                        <li>Recognizing the hour hand and minute hand</li>
                                        <li>Choosing the correct time from multiple options</li>
                                        <li>Building early time-telling confidence</li>
                                    </ul>

                                    <h2>🧠 Why Learning Time Matters at UKG Level</h2>
                                    <ul>
                                        <li>Introduces the concept of routines and schedules</li>
                                        <li>Builds logical thinking and number sense</li>
                                        <li>Improves visual interpretation of clock hands</li>
                                        <li>Prepares children for daily life tasks (school timings, meal times, etc.)</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/ukg/mathematics/smallest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Smallest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/biggest-number"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Biggest Number</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/ukg/mathematics/number-comparison"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Number Comparison</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>💡 Fun Learning Ideas at Home</h2>
                                    <ul>
                                        <li>Use a toy clock to set different times and ask your child to guess</li>
                                        <li>Talk about daily routine (e.g., “What time do we eat lunch?”)</li>
                                        <li>Use flashcards with clocks and matching times</li>
                                    </ul>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Choose the Correct Time</h1>

                                <p>
                                    Understanding time is a valuable life skill. This <strong>“Choose the Correct Time”</strong> activity helps UKG students identify and match time on analog clocks. With easy-to-read visuals and guided practice, your child will learn how to tell time in a fun and interactive way!
                                </p>

                                <h2>🕒 What Will Your Child Learn?</h2>
                                <ul>
                                    <li>Reading time from analog clocks (o&apos;clock times like 1:00, 2:00, 3:00, etc.)</li>
                                    <li>Recognizing the hour hand and minute hand</li>
                                    <li>Choosing the correct time from multiple options</li>
                                    <li>Building early time-telling confidence</li>
                                </ul>

                                <h2>🧠 Why Learning Time Matters at UKG Level</h2>
                                <ul>
                                    <li>Introduces the concept of routines and schedules</li>
                                    <li>Builds logical thinking and number sense</li>
                                    <li>Improves visual interpretation of clock hands</li>
                                    <li>Prepares children for daily life tasks (school timings, meal times, etc.)</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>💡 Fun Learning Ideas at Home</h2>
                                <ul>
                                    <li>Use a toy clock to set different times and ask your child to guess</li>
                                    <li>Talk about daily routine (e.g., “What time do we eat lunch?”)</li>
                                    <li>Use flashcards with clocks and matching times</li>
                                </ul>
                            </article>

                            {/* Clock Display */}
                            <div
                                style={{
                                    width: "200px",
                                    height: "200px",
                                    borderRadius: "50%",
                                    border: "5px solid black",
                                    margin: "20px auto",
                                    position: "relative",
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "white",
                                }}
                            >
                                {/* Clock Numbers */}
                                {[...Array(12)].map((_, i) => {
                                    const angle = (i + 1) * 30;
                                    const x = 85 + 80 * Math.sin((angle * Math.PI) / 180);
                                    const y = 85 - 80 * Math.cos((angle * Math.PI) / 180);

                                    return (
                                        <div
                                            key={i}
                                            style={{
                                                position: "absolute",
                                                left: `${x}px`,
                                                top: `${y}px`,
                                                fontSize: "16px",
                                                fontWeight: "bold",
                                            }}
                                        >
                                            {i + 1}
                                        </div>
                                    );
                                })}

                                {/* Hour Hand */}
                                <div
                                    style={{
                                        width: "5px",
                                        height: "50px",
                                        backgroundColor: "black",
                                        position: "absolute",
                                        left: "50%",
                                        bottom: "50%",
                                        transformOrigin: "bottom",
                                        transform: `rotate(${(time.hours % 12) * 30 + (time.minutes / 60) * 30}deg)`,
                                    }}
                                />

                                {/* Minute Hand */}
                                <div
                                    style={{
                                        width: "3px",
                                        height: "65px",
                                        backgroundColor: "black",
                                        position: "absolute",
                                        left: "50%",
                                        bottom: "50%",
                                        transformOrigin: "bottom",
                                        transform: `rotate(${time.minutes * 6}deg)`,
                                    }}
                                />
                            </div>

                            {/* Answer Options */}
                            <div style={{ display: "flex", justifyContent: "center", gap: "10px", flexWrap: "wrap" }}>
                                {options.map((option, index) => (
                                    <div className="background-blue"
                                        key={index}
                                        onClick={() => handleOptionClick(option)}
                                        style={{ margin: "5px", padding: "10px", minWidth: "60px" }}
                                    >
                                        {option}
                                    </div>
                                ))}
                            </div>

                            {/* Feedback */}
                            {feedback && <p>{feedback}</p>}
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default StaticClock;
